import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ttStatsAPI } from '../../services/api';
import { EloData } from '../../types/api.types';
import { cleanPlayerUrl } from '../../utils/urlUtils';
import WrappedCard from './WrappedCard';
import ProgressBar from './ProgressBar';
import IntroCard from './cards/IntroCard';
import StatsOverviewCard from './cards/StatsOverviewCard';
import EloJourneyCard from './cards/EloJourneyCard';
import RivalsCard from './cards/RivalsCard';
import StyleCard from './cards/StyleCard';
import GiantKillerCard from './cards/GiantKillerCard';
import ClutchMasterCard from './cards/ClutchMasterCard';
import FirstSetPowerCard from './cards/FirstSetPowerCard';
import ComebackStoryCard from './cards/ComebackStoryCard';
import AchievementsCard from './cards/AchievementsCard';
import MonthlyBreakdownCard from './cards/MonthlyBreakdownCard';
import FinaleCard from './cards/FinaleCard';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { wrappedTranslations } from '../../translations/wrapped';

const WrappedContainer: React.FC = () => {
    const { url, lang } = useParams<{ url: string; lang?: string }>();
    const navigate = useNavigate();
    const currentLang = (lang as 'en' | 'fr' | 'de' | 'it') || 'en';

    const [playerData, setPlayerData] = useState<EloData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [direction, setDirection] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const [cards, setCards] = useState<string[]>([]);
    const [cardDurations, setCardDurations] = useState<Record<string, number>>({});

    // Helper to parse dates
    const parseDate = (dateStr: string): Date => {
        if (!dateStr) return new Date();
        if (dateStr.includes('.')) {
            const parts = dateStr.split('.');
            if (parts.length === 3) {
                const [day, month, year] = parts;
                return new Date(`${year}-${month}-${day}`);
            }
        }
        return new Date(dateStr);
    };

    const loadPlayerData = async (playerUrl: string) => {
        setLoading(true);
        setError(null);

        try {
            const cleanUrl = cleanPlayerUrl(playerUrl);
            const data = await ttStatsAPI.getPlayerElo(cleanUrl);
            setPlayerData(data);
        } catch (err: any) {
            console.error('Error loading player data:', err);
            setError('Failed to load player data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (url) {
            loadPlayerData(url);
        }
    }, [url]);

    const nextCard = useCallback(() => {
        if (currentIndex < cards.length - 1) {
            setDirection(1);
            setCurrentIndex(prev => prev + 1);
        } else {
            // End of wrapped
            navigate(`/${currentLang}`);
        }
    }, [currentIndex, cards.length, navigate, currentLang]);

    const prevCard = useCallback(() => {
        if (currentIndex > 0) {
            setDirection(-1);
            setCurrentIndex(prev => prev - 1);
        }
    }, [currentIndex]);

    // Auto-advance
    useEffect(() => {
        if (loading || error || !cards.length || isPaused) return;

        const currentCardType = cards[currentIndex];
        const duration = cardDurations[currentCardType] || 5000;

        const timer = setTimeout(() => {
            nextCard();
        }, duration);

        return () => clearTimeout(timer);
    }, [currentIndex, cards, cardDurations, loading, error, isPaused, nextCard]);

    // Keyboard navigation
    const handleKeyDown = useCallback((e: KeyboardEvent) => {
        if (e.key === 'ArrowRight' || e.key === 'Space') {
            nextCard();
        } else if (e.key === 'ArrowLeft') {
            prevCard();
        }
    }, [nextCard, prevCard]);

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    // Update cards array when playerData changes
    useEffect(() => {
        if (!playerData) return;

        // Get rival data
        const opponentCounts: Record<string, { count: number; wins: number; losses: number }> = {};
        playerData.games?.forEach((game: any) => {
            const opponentName = `${game.enemy_first_name} ${game.enemy_last_name}`;
            if (!opponentCounts[opponentName]) {
                opponentCounts[opponentName] = { count: 0, wins: 0, losses: 0 };
            }
            opponentCounts[opponentName].count++;
            if (game.win) {
                opponentCounts[opponentName].wins++;
            } else {
                opponentCounts[opponentName].losses++;
            }
        });
        const topRival = Object.entries(opponentCounts)
            .sort(([, a], [, b]) => b.count - a.count)[0];
        const rivalData = topRival ? {
            name: topRival[0],
            wins: topRival[1].wins,
            losses: topRival[1].losses,
            total: topRival[1].count
        } : null;

        // Check if we have sufficient set stats data
        const matchesWithScores = playerData.set_stats?.total_matches_with_scores || 0;
        const hasSetStats = matchesWithScores >= 5; // Need at least 5 matches with scores for reliable stats

        // Dynamically build cards array based on available data
        const newCards = [
            'intro',
            'year_overview',
            'elo_journey',
            ...(hasSetStats ? ['playing_style'] : []),
            'giant_killer',
            ...(hasSetStats ? ['clutch_master'] : []),
            ...(rivalData && rivalData.total >= 2 ? ['rivals'] : []),
            ...(hasSetStats ? ['first_set_power'] : []),
            ...(hasSetStats ? ['comeback_story'] : []),
            'achievements',
            'monthly_breakdown',
            'finale',
        ];

        setCards(newCards);

        // Duration for each card in milliseconds
        setCardDurations({
            intro: 5000,
            year_overview: 8000,
            elo_journey: 10000,
            playing_style: 8000,
            giant_killer: 8000,
            clutch_master: 8000,
            rivals: 7000,
            first_set_power: 7000,
            comeback_story: 7000,
            achievements: 8000,
            monthly_breakdown: 8000,
            finale: 15000,
        });
    }, [playerData]);

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-gray-50 via-white to-gray-50 flex items-center justify-center">
                <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    className="w-16 h-16 border-4 border-gray-200 border-t-[#1C7AD6] rounded-full"
                />
            </div>
        );
    }

    if (error || !playerData) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-gray-50 via-white to-gray-50 flex items-center justify-center p-4">
                <div className="text-center bg-white rounded-2xl border border-gray-200 shadow-lg p-8 max-w-md">
                    <h1 className="text-3xl font-bold text-gray-900 mb-4">Oops!</h1>
                    <p className="text-gray-600 mb-8">We couldn't load your Wrapped data.</p>
                    <Link
                        to={`/${currentLang}`}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-[#1C7AD6] hover:bg-[#1565C0] text-white rounded-xl transition-colors font-medium"
                    >
                        Back to Home
                    </Link>
                </div>
            </div>
        );
    }

    // Helper to get display name
    const getDisplayName = () => {
        let name = playerData.player_name || 'Player';
        if (name.includes(',')) {
            const [last, first] = name.split(',').map(s => s.trim());
            name = `${first} ${last}`;
        }
        return name;
    };

    // Helper to get peak ELO
    const getPeakElo = () => {
        if (playerData.elo_history && playerData.elo_history.length > 0) {
            return Math.max(...playerData.elo_history.map(e => e.elo));
        }
        return playerData.current_elo || 0;
    };

    // Helper to get lowest ELO
    const getLowestElo = () => {
        if (playerData.elo_history && playerData.elo_history.length > 0) {
            return Math.min(...playerData.elo_history.map(e => e.elo));
        }
        return playerData.current_elo || 0;
    };

    // Logic for Playing Style
    const getPlayingStyle = (): { title: string; description: string; iconType: 'trophy' | 'flame' | 'zap' | 'trending' | 'shield'; stats: any[] } => {
        const t = wrappedTranslations[currentLang];
        const cleanSheetPct = playerData.set_stats?.dominance?.clean_sheet_percentage || 0;
        const closeWinPct = playerData.set_stats?.clutch?.close_win_percentage || 0;
        const closeMatches = playerData.set_stats?.clutch?.close_matches || 0;
        const comebackWins = playerData.set_stats?.comebacks?.comeback_wins || 0;
        const totalWins = playerData.stats?.wins || 0;

        if (cleanSheetPct > 40) {
            return {
                title: t.theDominator,
                description: t.theDominatorDesc,
                iconType: "trophy" as const,
                stats: [
                    { label: t.cleanSheets, value: `${cleanSheetPct.toFixed(0)}%`, color: "text-[#1C7AD6]", subtext: t.ofWinsWere30 },
                    { label: t.totalWins, value: totalWins, color: "text-gray-900", subtext: t.matchesWon }
                ]
            };
        } else if (closeWinPct > 60 && closeMatches > 5) {
            return {
                title: t.iceCold,
                description: t.iceColdDesc,
                iconType: "flame" as const,
                stats: [
                    { label: t.clutchWinRate, value: `${closeWinPct.toFixed(0)}%`, color: "text-[#1C7AD6]", subtext: t.in5Setters },
                    { label: t.closeMatches, value: closeMatches, color: "text-gray-900", subtext: t.decidersPlayed }
                ]
            };
        } else if (comebackWins > 2) {
            return {
                title: t.thePhoenix,
                description: t.thePhoenixDesc,
                iconType: "zap" as const,
                stats: [
                    { label: t.comebacks, value: comebackWins, color: "text-emerald-600", subtext: t.winsFromBehind },
                    { label: t.mentalStrength, value: "100%", color: "text-gray-900", subtext: t.neverGiveUp }
                ]
            };
        } else {
            return {
                title: t.theTactician,
                description: t.theTacticianDesc,
                iconType: "trending" as const,
                stats: [
                    { label: t.winRate, value: `${playerData.stats?.win_percentage?.toFixed(0)}%`, color: "text-emerald-600", subtext: t.overallPerformance },
                    { label: t.totalGames, value: playerData.stats?.total_games, color: "text-gray-900", subtext: t.matchesPlayedLower }
                ]
            };
        }
    };

    // Logic for Rival (re-calculated for render)
    const getRivalData = () => {
        const opponentCounts: Record<string, { count: number; wins: number; losses: number }> = {};
        playerData.games?.forEach((game: any) => {
            const opponentName = `${game.enemy_first_name} ${game.enemy_last_name}`;
            if (!opponentCounts[opponentName]) {
                opponentCounts[opponentName] = { count: 0, wins: 0, losses: 0 };
            }
            opponentCounts[opponentName].count++;
            if (game.win) {
                opponentCounts[opponentName].wins++;
            } else {
                opponentCounts[opponentName].losses++;
            }
        });
        const topRival = Object.entries(opponentCounts)
            .sort(([, a], [, b]) => b.count - a.count)[0];
        return topRival ? {
            name: topRival[0],
            wins: topRival[1].wins,
            losses: topRival[1].losses,
            total: topRival[1].count
        } : null;
    };

    // Logic for Giant Killer (Best Win)
    const getBestWin = () => {
        let bestWin = null;
        let maxEloDiff = -Infinity;
        let vsHigherWins = 0;

        playerData.games?.forEach((game: any) => {
            if (game.win && game.enemy_elo) {
                // Calculate player ELO at time of game (approximate using month_start_elo)
                const playerEloAtTime = game.month_start_elo || playerData.current_elo || 0;

                if (game.enemy_elo > playerEloAtTime) {
                    vsHigherWins++;
                }

                // Best win is the one with highest ELO difference
                const eloDiff = game.enemy_elo - playerEloAtTime;
                if (eloDiff > maxEloDiff) {
                    maxEloDiff = eloDiff;
                    bestWin = game;
                }
            }
        });
        return { bestWin, vsHigherWins };
    };

    // Logic for Clutch Master (Streak)
    const getBestStreak = () => {
        let currentStreak = 0;
        let maxStreak = 0;
        let streakEloGain = 0;
        let currentStreakEloGain = 0;

        const sortedGames = [...(playerData.games || [])].sort((a: any, b: any) =>
            parseDate(a.date).getTime() - parseDate(b.date).getTime()
        );

        sortedGames.forEach((game: any) => {
            if (game.win) {
                currentStreak++;
                // Use elo_delta which is the actual ELO change for the game
                const eloChange = Math.abs(game.elo_delta || 0);
                currentStreakEloGain += eloChange;
                if (currentStreak > maxStreak) {
                    maxStreak = currentStreak;
                    streakEloGain = currentStreakEloGain;
                }
            } else {
                currentStreak = 0;
                currentStreakEloGain = 0;
            }
        });

        return { count: maxStreak, elo_gained: Math.round(streakEloGain) };
    };

    // Logic for Monthly Breakdown
    const getMonthlyData = () => {
        const months: Record<string, { wins: number; losses: number; eloChange: number }> = {};

        playerData.games?.forEach((game: any) => {
            const date = parseDate(game.date);
            const monthKey = date.toLocaleString('default', { month: 'short' });

            if (!months[monthKey]) {
                months[monthKey] = { wins: 0, losses: 0, eloChange: 0 };
            }

            if (game.win) {
                months[monthKey].wins++;
            } else {
                months[monthKey].losses++;
            }
            months[monthKey].eloChange += (game.elo_delta || 0);
        });

        const monthOrder = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return Object.entries(months)
            .map(([month, data]) => ({
                month,
                games: data.wins + data.losses,
                wins: data.wins
            }))
            .sort((a, b) => monthOrder.indexOf(a.month) - monthOrder.indexOf(b.month));
    };

    // Calculate total ELO change from all games
    const getTotalEloChange = () => {
        let totalChange = 0;
        playerData.games?.forEach((game: any) => {
            totalChange += (game.elo_delta || 0);
        });
        return Math.round(totalChange);
    };

    const playingStyle = getPlayingStyle();
    const rivalData = getRivalData();
    const { bestWin, vsHigherWins } = getBestWin();
    const bestStreak = getBestStreak();
    const monthlyData = getMonthlyData();
    const totalEloChange = getTotalEloChange();

    return (
        <div
            className="fixed inset-0 bg-gradient-to-b from-gray-50 via-white to-gray-50 overflow-hidden font-sans"
            onMouseDown={() => setIsPaused(true)}
            onMouseUp={() => setIsPaused(false)}
            onTouchStart={() => setIsPaused(true)}
            onTouchEnd={() => setIsPaused(false)}
        >
            {/* Animated Background */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                {/* Base Background */}
                <div className="absolute inset-0 bg-gray-50" />

                {/* Moving Orbs - Blue Theme */}
                <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-[#1C7AD6]/8 rounded-full blur-[120px] animate-pulse" style={{ animationDuration: '8s' }} />
                <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-[#1C7AD6]/8 rounded-full blur-[120px] animate-pulse" style={{ animationDuration: '10s', animationDelay: '1s' }} />

                {/* Subtle Texture Overlay */}
                <div className="absolute inset-0 opacity-[0.02] bg-[url('https://grainy-gradients.vercel.app/noise.svg')] mix-blend-overlay" />
            </div>

            <ProgressBar
                total={cards.length}
                current={currentIndex}
                onJumpTo={(index) => {
                    setDirection(index > currentIndex ? 1 : -1);
                    setCurrentIndex(index);
                }}
            />

            <button
                onClick={() => navigate(`/${currentLang}`)}
                className="absolute top-4 right-4 z-50 p-2 bg-white/80 hover:bg-white text-gray-700 hover:text-gray-900 rounded-full backdrop-blur-sm transition-colors shadow-sm"
                aria-label="Close"
            >
                <X className="w-6 h-6" />
            </button>

            <AnimatePresence initial={false} custom={direction} mode="wait">
                <WrappedCard
                    key={cards[currentIndex]}
                    direction={direction}
                    onNext={nextCard}
                    onPrev={prevCard}
                >
                    {(() => {
                        const cardType = cards[currentIndex];
                        switch (cardType) {
                            case 'intro':
                                return (
                                    <IntroCard
                                        name={getDisplayName()}
                                        club={playerData.club || ''}
                                        classification={playerData.classification || ''}
                                        swissRank={playerData.swissRank}
                                        lang={currentLang}
                                    />
                                );
                            case 'year_overview':
                                return (
                                    <StatsOverviewCard
                                        totalGames={playerData.stats?.total_games || 0}
                                        winPercentage={playerData.stats?.win_percentage || 0}
                                        eloChange={totalEloChange}
                                        peakElo={getPeakElo()}
                                        lang={currentLang}
                                    />
                                );
                            case 'elo_journey':
                                return (
                                    <EloJourneyCard
                                        eloHistory={playerData.elo_history || []}
                                        currentElo={playerData.current_elo || 0}
                                        peakElo={getPeakElo()}
                                        lowestElo={getLowestElo()}
                                        eloChange={totalEloChange}
                                        lang={currentLang}
                                    />
                                );
                            case 'playing_style':
                                return (
                                    <StyleCard
                                        title={playingStyle.title}
                                        description={playingStyle.description}
                                        iconType={playingStyle.iconType}
                                        stats={playingStyle.stats}
                                        lang={currentLang}
                                    />
                                );
                            case 'giant_killer':
                                return (
                                    <GiantKillerCard
                                        bestWin={bestWin ? {
                                            opponent: `${(bestWin as any).enemy_first_name} ${(bestWin as any).enemy_last_name}`,
                                            enemy_elo: (bestWin as any).enemy_elo,
                                            elo_gain: Math.abs((bestWin as any).elo_delta || 0)
                                        } : null}
                                        currentElo={(bestWin as any)?.month_start_elo || playerData.current_elo || 0}
                                        vsHigherWins={vsHigherWins}
                                        lang={currentLang}
                                    />
                                );
                            case 'clutch_master':
                                return (
                                    <ClutchMasterCard
                                        closeWinPercentage={playerData.set_stats?.clutch?.close_win_percentage || 0}
                                        closeWins={playerData.set_stats?.clutch?.close_wins || 0}
                                        closeMatches={playerData.set_stats?.clutch?.close_matches || 0}
                                        bestStreak={bestStreak}
                                        lang={currentLang}
                                    />
                                );
                            case 'rivals':
                                return (
                                    <RivalsCard
                                        rivalName={rivalData?.name || ''}
                                        wins={rivalData?.wins || 0}
                                        losses={rivalData?.losses || 0}
                                        totalMatches={rivalData?.total || 0}
                                        lang={currentLang}
                                    />
                                );
                            case 'first_set_power':
                                return (
                                    <FirstSetPowerCard
                                        winRateWhenWon={playerData.set_stats?.first_set?.match_win_rate_when_won || 0}
                                        firstSetWins={playerData.set_stats?.first_set?.wins || 0}
                                        totalMatches={playerData.stats?.total_games || 0}
                                        lang={currentLang}
                                    />
                                );
                            case 'comeback_story':
                                return (
                                    <ComebackStoryCard
                                        comebackWins={playerData.set_stats?.comebacks?.comeback_wins || 0}
                                        reverseSweeps={playerData.set_stats?.comebacks?.reverse_sweeps || 0}
                                        lang={currentLang}
                                    />
                                );
                            case 'achievements':
                                return (
                                    <AchievementsCard
                                        achievements={playerData.achievements || []}
                                        lang={currentLang}
                                    />
                                );
                            case 'monthly_breakdown':
                                return (
                                    <MonthlyBreakdownCard
                                        monthlyData={monthlyData}
                                        lang={currentLang}
                                    />
                                );
                            case 'finale':
                                return (
                                    <FinaleCard
                                        name={getDisplayName()}
                                        club={playerData.club || ''}
                                        classification={playerData.classification || ''}
                                        totalGames={playerData.stats?.total_games || 0}
                                        winPercentage={playerData.stats?.win_percentage || 0}
                                        peakElo={getPeakElo()}
                                        bestStreak={bestStreak.count}
                                        lang={currentLang}
                                    />
                                );
                            default:
                                return null;
                        }
                    })()}
                </WrappedCard>
            </AnimatePresence>
        </div>
    );
};

export default WrappedContainer;
